/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  Box,
  TextField,
  Typography,
  Stack,
  useTheme,
  InputAdornment,
  SvgIcon,
  IconButton,
} from "@mui/material";
import CheckedIcon from "@/assets/icons/checked.webp";
import UnCheckedIcon from "@/assets/icons/unchecked.webp";
import { ReactComponent as InputArrowHeadUp } from "@/assets/icons/Input/Number_Arrow_Head_Up.svg";
import { ReactComponent as InputArrowHeadDown } from "@/assets/icons/Input/Number_Arrow_Head_Down.svg";
import { BigNumber, ethers } from "ethers";
import { enqueueSnackbar } from "notistack";

interface InputGroupProps {
  children: React.ReactNode;
  sx?: any;
  error?: string;
}

const InputGroup = ({ children, sx, error }: InputGroupProps) => {
  const theme = useTheme();
  return (
    <>
      <Box mb="29px" sx={sx}>
        {children}
      </Box>
      {error && (
        <Typography
          variant="body2"
          fontSize="0.75rem"
          color={theme.colors.errorFontColor}
          lineHeight="1.244375rem"
          mx="0.875rem"
          mt="0.1875rem"
          mb="1.875rem"
        >
          {error}
        </Typography>
      )}
    </>
  );
};

type InputLabelProps = {
  htmlFor: string;
  children: React.ReactNode;
  sx?: any;
  ContinaerSx?: any;
  checked?: boolean;
};

const InputLabel = ({
  htmlFor,
  children,
  sx,
  checked,
  ContinaerSx,
}: InputLabelProps) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      gap="0.5rem"
      mb="15px"
      alignItems="center"
      sx={ContinaerSx}
    >
      <Typography
        component="label"
        htmlFor={htmlFor}
        variant="body3"
        color={theme.colors.unFocused}
        sx={{ color: theme.colors.unFocused, ...sx }}
      >
        {children}
      </Typography>
      {typeof checked === "boolean" && (
        <Box
          component="img"
          src={checked ? CheckedIcon : UnCheckedIcon}
          width="1.25rem"
          height="1.25rem"
        />
      )}
    </Stack>
  );
};

interface PhoneInputProps {
  name: string;
  id: string;
  valueNumber: string;
  valueCode: string;
  onChangeNumber: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeCode: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error: string;
  numberPlaceholder: string;
  codePlaceholder: string;
}

const PhoneInput = ({
  name,
  id,
  valueNumber,
  valueCode,
  onChangeNumber,
  onChangeCode,
  error,
  numberPlaceholder,
  codePlaceholder,
}: PhoneInputProps) => {
  const theme = useTheme();
  return (
    <>
      <Stack direction="row">
        <TextField
          variant="outlined"
          fullWidth
          type="number"
          name={name + "_code"}
          id={id + "_code"}
          placeholder={codePlaceholder}
          value={valueCode}
          onChange={onChangeCode}
          InputProps={{
            inputProps: { min: 0 },
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="body1">+</Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "6rem",
            "& .MuiInputBase-root": {
              borderRadius: `${theme.borderRadius.main} 0 0 ${theme.borderRadius.main}`,
            },
            "& .MuiInputBase-input": {
              py: "1.0625rem",
              px: "0",
              color: theme.typography.body2.color,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #9C9C9C",
              borderRight: "none",
            },
            "&:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid #6C6C6C !important",
                borderRight: "none",
              },
          }}
        />
        <TextField
          variant="outlined"
          fullWidth
          type="number"
          name={name}
          id={id}
          placeholder={numberPlaceholder}
          value={valueNumber}
          onChange={onChangeNumber}
          InputProps={{
            inputProps: { min: 0 },
          }}
          sx={{
            "& .MuiInputBase-root": {
              borderRadius: `0 ${theme.borderRadius.main} ${theme.borderRadius.main} 0`,
            },
            "& .MuiInputBase-input": {
              pl: "0.5rem",
              color: theme.typography.body2.color,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #9C9C9C",
            },
            "&:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid #6C6C6C !important",
              },
          }}
        />
      </Stack>
      {error && (
        <Typography
          variant="body2"
          fontSize="0.75rem"
          color={theme.colors.errorFontColor}
          lineHeight="1.244375rem"
          mx="0.875rem"
          mt="0.1875rem"
        >
          {error}
        </Typography>
      )}
    </>
  );
};

interface NumberInputProps {
  name: string;
  id: string;
  value: string;
  onChange?: (e: { target: { value: string } }) => void;
  children?: React.ReactNode;
  error?: string;
  placeholder?: string;
  label?: string;
  sx?: any;
  required?: boolean;
}
const NumberInput = ({
  name,
  id,
  value,
  onChange,
  children,
  error,
  placeholder,
  label,
  sx,
  required = false,
}: NumberInputProps) => {
  const isDisabled = onChange === undefined;
  return (
    <TextField
      variant="outlined"
      fullWidth
      type="number"
      required={required}
      name={name}
      id={id}
      label={label}
      placeholder={placeholder}
      value={value}
      error={!!error}
      helperText={error}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
          if (e.target.value) {
            try {
              ethers.utils.parseEther(e.target.value);
              onChange({
                target: {
                  value: e.target.value,
                },
              });
            } catch (error: any) {
              if (
                error.message.includes("fractional component exceeds decimals")
              ) {
                enqueueSnackbar(
                  "Please enter a valid number with up to 18 decimal places.",
                  { variant: "error" },
                );
              }
            }
          } else {
            onChange({
              target: {
                value: e.target.value,
              },
            });
          }
        }
      }}
      disabled={isDisabled}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Stack direction="column" gap="5.54px">
              <IconButton
                sx={{ p: "0" }}
                onClick={() => {
                  if (onChange) {
                    if (value) {
                      const weiAmount = ethers.utils.parseEther(value);
                      onChange({
                        target: {
                          value: ethers.utils.formatEther(
                            BigNumber.from(weiAmount)
                              .add(ethers.utils.parseEther("1"))
                              .toString(),
                          ),
                        },
                      });
                    } else {
                      onChange({
                        target: {
                          value: "1",
                        },
                      });
                    }
                  }
                }}
              >
                <SvgIcon
                  component={InputArrowHeadUp}
                  sx={{ width: "14.61px", height: "6.55px", fill: "none" }}
                  inheritViewBox
                />
              </IconButton>
              <IconButton
                sx={{ p: "0" }}
                onClick={() => {
                  if (onChange) {
                    if (value) {
                      const weiAmount = ethers.utils.parseEther(value);
                      if (
                        BigNumber.from(weiAmount).gte(
                          BigNumber.from(ethers.utils.parseEther("1")),
                        )
                      ) {
                        onChange({
                          target: {
                            value: ethers.utils.formatEther(
                              BigNumber.from(weiAmount)
                                .sub(ethers.utils.parseEther("1"))
                                .toString(),
                            ),
                          },
                        });
                      }
                    } else {
                      onChange({
                        target: {
                          value: "0",
                        },
                      });
                    }
                  }
                }}
              >
                <SvgIcon
                  component={InputArrowHeadDown}
                  sx={{ width: "14.61px", height: "6.55px", fill: "none" }}
                  inheritViewBox
                />
              </IconButton>
            </Stack>
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiInputBase-root": {
          paddingRight: "31.69px",
        },
        "& .MuiInputBase-input": {
          MozAppearance: "textfield",
        },
        ...sx,
      }}
    >
      {children}
    </TextField>
  );
};

interface FormProps {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  children: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sx?: any;
}

const Form = ({ onSubmit, children, sx }: FormProps) => {
  return (
    <Box component={"form"} onSubmit={onSubmit} height="100%" sx={sx}>
      {children}
    </Box>
  );
};

export { InputGroup, InputLabel, NumberInput, Form, PhoneInput };
